import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Palmtree, Loader2, Download, ArrowLeft, Sparkles, Sun, Moon, Coffee, Bed, Bus, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import EmailGate, { useEmailGate } from '../../components/EmailGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  'Beaches', 'Food', 'Culture', 'Adventure', 'Nightlife',
  'Nature', 'History', 'Shopping', 'Spa', 'Photography',
];

export default function HolidayPlannerPage() {
  const navigate = useNavigate();
  const [destination, setDestination] = useState('Bali, Indonesia');
  const [days, setDays] = useState(5);
  const [travellers, setTravellers] = useState(2);
  const [budget, setBudget] = useState('mid-range');
  const [interests, setInterests] = useState(['Beaches', 'Food', 'Culture']);
  const [notes, setNotes] = useState('Honeymoon trip, prefer boutique hotels.');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const pdfRef = useRef(null);
  const gate = useEmailGate('holiday-planner');

  const toggleInterest = (v) =>
    setInterests((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const generate = async () => {
    if (!destination.trim()) return toast({ title: 'Where are we going?' });
    setLoading(true);
    setPlan(null);
    try {
      const r = await fetch(`${API}/templates/holiday-planner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destination,
          days: Number(days),
          travellers: Number(travellers),
          budget_tier: budget,
          interests,
          notes: notes || undefined,
        }),
      });
      if (!r.ok) throw new Error(`Server error (${r.status})`);
      const data = await r.json();
      setPlan(data);
    } catch (e) {
      toast({ title: 'Generation failed', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    gate.run(async () => {
      if (!pdfRef.current) return;
      toast({ title: 'Preparing PDF…' });
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        backgroundColor: '#0b1120',
        useCORS: true,
      });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      const pageH = pdf.internal.pageSize.getHeight();
      // Multi-page support
      let heightLeft = pdfH;
      let position = 0;
      pdf.addImage(img, 'PNG', 0, position, pdfW, pdfH, undefined, 'FAST');
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position = heightLeft - pdfH;
        pdf.addPage();
        pdf.addImage(img, 'PNG', 0, position, pdfW, pdfH, undefined, 'FAST');
        heightLeft -= pageH;
      }
      pdf.save(`${(plan?.title || 'holiday').replace(/\s+/g, '_')}.pdf`);
      toast({ title: 'PDF downloaded ✅' });
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0611] text-white">
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button
            data-testid="holiday-back"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-amber-300 font-semibold">Holiday Planner</span>
          <div className="ml-auto">
            <BrandLogo size={24} tone="light" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[400px_1fr] gap-8">
        {/* LEFT: form */}
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <Palmtree size={13} className="text-amber-300" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">
                Holiday Planner
              </span>
            </div>
            <h1 className="text-3xl font-black tracking-tight">Design your dream trip</h1>
            <p className="text-sm text-white/60 mt-1.5">
              We&rsquo;ll craft a day-by-day itinerary with real venues, hotels and restaurants.
            </p>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Destination *</label>
            <input
              data-testid="holiday-destination"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Bali, Indonesia"
              className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-amber-400 outline-none text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Days</label>
              <input
                data-testid="holiday-days"
                type="number"
                min={1}
                max={30}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-amber-400 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Travellers</label>
              <input
                data-testid="holiday-travellers"
                type="number"
                min={1}
                max={20}
                value={travellers}
                onChange={(e) => setTravellers(e.target.value)}
                className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-amber-400 outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Budget</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {['budget', 'mid-range', 'luxury'].map((b) => (
                <button
                  key={b}
                  data-testid={`holiday-budget-${b}`}
                  onClick={() => setBudget(b)}
                  className={`h-11 rounded-xl text-[11px] font-bold uppercase tracking-widest transition ${
                    budget === b
                      ? 'bg-gradient-to-r from-amber-400 to-rose-500 text-white shadow-lg'
                      : 'bg-white/5 border border-white/15 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Interests</label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {INTEREST_OPTIONS.map((i) => (
                <button
                  key={i}
                  onClick={() => toggleInterest(i)}
                  className={`text-[11px] px-2.5 py-1.5 rounded-full font-semibold transition ${
                    interests.includes(i)
                      ? 'bg-amber-400/20 border border-amber-400/50 text-amber-200'
                      : 'bg-white/5 border border-white/15 text-white/70 hover:bg-white/10'
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-2 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-amber-400 outline-none text-sm"
            />
          </div>

          <Button
            data-testid="holiday-generate"
            onClick={generate}
            disabled={loading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 text-white font-bold shadow-[0_20px_50px_-20px_rgba(251,146,60,0.6)]"
          >
            {loading ? (
              <><Loader2 className="mr-2 animate-spin" size={16} /> Planning…</>
            ) : (
              <><Sparkles className="mr-2" size={16} /> Generate itinerary</>
            )}
          </Button>
        </div>

        {/* RIGHT: Plan */}
        <div>
          {!plan && !loading && (
            <div className="border-2 border-dashed border-white/10 rounded-2xl p-16 text-center text-white/50">
              <Palmtree size={40} className="mx-auto text-white/25 mb-4" />
              <div className="font-bold text-white/80">Your holiday plan appears here</div>
              <div className="text-sm mt-2">Fill in the details and hit Generate.</div>
            </div>
          )}
          {loading && (
            <div className="border-2 border-dashed border-amber-400/30 rounded-2xl p-16 text-center">
              <Loader2 size={40} className="mx-auto text-amber-300 mb-4 animate-spin" />
              <div className="font-bold">Curating your holiday…</div>
              <div className="text-sm text-white/60 mt-2">Selecting hotels, restaurants and activities</div>
            </div>
          )}
          {plan && (
            <div>
              <div className="flex justify-end mb-4">
                <Button
                  data-testid="holiday-download-pdf"
                  onClick={downloadPDF}
                  className="rounded-xl bg-white text-slate-900 font-bold h-11"
                >
                  <Download size={16} className="mr-2" /> Download PDF
                </Button>
              </div>
              <HolidayDoc data={plan} innerRef={pdfRef} />
            </div>
          )}
        </div>
      </div>
      <Toaster />
      <EmailGate {...gate} />
    </div>
  );
}

function HolidayDoc({ data, innerRef }) {
  return (
    <div
      ref={innerRef}
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0b0714 0%, #0a0611 100%)',
        boxShadow: '0 30px 80px -30px rgba(0,0,0,0.8)',
      }}
    >
      {/* Cover */}
      <div
        className="p-8 border-b border-white/10"
        style={{
          background:
            'linear-gradient(135deg, rgba(251,146,60,0.35), rgba(244,63,94,0.35) 50%, rgba(192,38,211,0.3))',
        }}
      >
        <div className="text-[10px] tracking-[0.4em] uppercase text-amber-100 font-bold mb-2">
          Around the World AI &middot; Holiday Package
        </div>
        <div className="text-3xl md:text-4xl font-black tracking-tight text-white">
          {data.title}
        </div>
        <div className="mt-2 text-white/85 text-sm max-w-2xl">{data.hero_line}</div>
        <div className="mt-5 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest font-bold">
          <span className="px-2 py-1 rounded-full bg-white/12 border border-white/25">
            {data.destination}
          </span>
          <span className="px-2 py-1 rounded-full bg-white/12 border border-white/25">
            {data.duration_days} days
          </span>
          <span className="px-2 py-1 rounded-full bg-white/12 border border-white/25">
            {data.travellers} travellers
          </span>
          <span className="px-2 py-1 rounded-full bg-white/12 border border-white/25">
            {data.budget_tier}
          </span>
          <span className="px-2 py-1 rounded-full bg-white text-slate-900">
            {data.total_budget_estimate}
          </span>
        </div>
      </div>

      {/* Days */}
      <div className="p-8 space-y-4">
        {(data.days || []).map((d) => (
          <div
            key={d.day}
            className="rounded-xl border border-white/10 overflow-hidden"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.15))' }}
          >
            <div className="px-5 py-3 border-b border-white/8 bg-black/25 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center font-black text-white text-lg">
                {d.day}
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold">
                  Day {d.day}
                </div>
                <div className="font-bold text-white text-lg leading-tight tracking-tight">
                  {d.title}
                </div>
              </div>
            </div>

            <div className="p-5 grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="flex items-center gap-1.5 text-amber-300 font-bold text-[10px] uppercase tracking-widest mb-1.5">
                  <Sun size={12} /> Morning
                </div>
                <div className="text-white/85 leading-relaxed">{d.morning}</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-orange-300 font-bold text-[10px] uppercase tracking-widest mb-1.5">
                  <Coffee size={12} /> Afternoon
                </div>
                <div className="text-white/85 leading-relaxed">{d.afternoon}</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-fuchsia-300 font-bold text-[10px] uppercase tracking-widest mb-1.5">
                  <Moon size={12} /> Evening
                </div>
                <div className="text-white/85 leading-relaxed">{d.evening}</div>
              </div>
            </div>

            {(d.meals?.length > 0 || d.accommodation || d.transport) && (
              <div className="px-5 py-3 border-t border-white/8 bg-black/15 grid md:grid-cols-3 gap-3 text-[11px]">
                {d.meals?.length > 0 && (
                  <div>
                    <div className="text-white/40 font-bold uppercase tracking-widest mb-1">Meals</div>
                    {d.meals.map((m, i) => (
                      <div key={i} className="text-white/85">
                        <span className="capitalize font-bold">{m.type}:</span> {m.restaurant}{' '}
                        <span className="text-white/45">({m.cuisine}, {m.price})</span>
                      </div>
                    ))}
                  </div>
                )}
                {d.accommodation && (
                  <div>
                    <div className="text-white/40 font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                      <Bed size={11} /> Stay
                    </div>
                    <div className="text-white/85">
                      <div className="font-bold">{d.accommodation.name}</div>
                      <div className="text-white/60">
                        {d.accommodation.type} &middot; {d.accommodation.price_per_night}
                      </div>
                    </div>
                  </div>
                )}
                {d.transport && (
                  <div>
                    <div className="text-white/40 font-bold uppercase tracking-widest mb-1 flex items-center gap-1">
                      <Bus size={11} /> Transport
                    </div>
                    <div className="text-white/85">{d.transport}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {(data.tips?.length > 0 || data.packing_essentials?.length > 0) && (
        <div className="px-8 pb-8 grid md:grid-cols-2 gap-4">
          {data.tips?.length > 0 && (
            <div className="rounded-xl border border-amber-400/25 bg-amber-500/8 p-4">
              <div className="text-[10px] tracking-widest uppercase text-amber-300 font-bold mb-2 flex items-center gap-1">
                <MapPin size={11} /> Local tips
              </div>
              <ul className="space-y-1.5 text-sm text-white/85">
                {data.tips.map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-amber-300">◆</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.packing_essentials?.length > 0 && (
            <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/8 p-4">
              <div className="text-[10px] tracking-widest uppercase text-cyan-300 font-bold mb-2">
                Packing essentials
              </div>
              <ul className="space-y-1.5 text-sm text-white/85">
                {data.packing_essentials.map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-cyan-300">◆</span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="px-8 pb-8 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/40 font-bold">
        <div>Powered by Around the World AI</div>
        <div>aroundtheworld.ai</div>
      </div>
    </div>
  );
}
